// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * What the change forms say about the time typed (design doc §8.6 step 6, US-C-07).
 *
 * * Before `changes_not_before` — the filing of this archive or of one it is fitted in, each
 *   of which the change gives a version — it cannot have happened: refused, and the form
 *   holds it rather than letting the server refuse it after everything else is filled in.
 * * Before `last_changed_at` it is a late record — work done earlier and recorded now. It is
 *   accepted, and said so, because it will be listed above changes that happened after it:
 *   the history is in the order changes are recorded.
 * * More than `FUTURE_ALLOWANCE_MS` ahead of now it has not happened yet: refused, as the
 *   server refuses it.
 * * A change reaching into a fitted module versions that module too, so it cannot predate the
 *   module's filing either. Which modules it reaches is known only once the parts are picked:
 *   `filedAfter` finds the one it would predate, for the form to hold the submit.
 *
 * Times are `YYYY-MM-DDTHH:mm`, local; `""` means the time of submission, which is none of
 * these — it is sent as no time at all, and the server dates it by its own clock.
 * A time not yet valid (`null`) is the field's own problem, not judged here.
 */

import type { TFunction } from "i18next";
import type { ArchiveDetail, ModuleRef } from "@/api/types";

/** How far ahead of now a typed time may be: the server's allowance for clock drift. */
export const FUTURE_ALLOWANCE_MS = 5 * 60 * 1000;

export function isEarlierThan(occurredAt: string | null, iso: string | null | undefined): boolean {
  if (!occurredAt || !iso) return false;
  return new Date(occurredAt).getTime() < Date.parse(iso);
}

export interface OccurredAtNote {
  /** Not to be submitted. */
  blocked: boolean;
  className: string;
  /** In place of the field's usual hint; null keeps it. */
  text: string | null;
}

export function occurredAtNote(
  occurredAt: string | null,
  archive: Pick<ArchiveDetail, "changes_not_before" | "last_changed_at">,
  t: TFunction,
  { lateRecords = true, now = Date.now() }: { lateRecords?: boolean; now?: number } = {},
): OccurredAtNote {
  const at = (iso: string | null | undefined) => new Date(iso ?? "").toLocaleString();
  if (occurredAt && new Date(occurredAt).getTime() > now + FUTURE_ALLOWANCE_MS) {
    return {
      blocked: true,
      className: "hint hint-error",
      text: t("maintenance.occurredInFuture", { time: new Date(now).toLocaleString() }),
    };
  }
  if (isEarlierThan(occurredAt, archive.changes_not_before)) {
    return {
      blocked: true,
      className: "hint hint-error",
      text: t("maintenance.occurredBeforeFiling", { time: at(archive.changes_not_before) }),
    };
  }
  if (lateRecords && isEarlierThan(occurredAt, archive.last_changed_at)) {
    return {
      blocked: false,
      className: "hint hint-warn",
      text: t("maintenance.occurredBeforeLast", { time: at(archive.last_changed_at) }),
    };
  }
  return { blocked: false, className: "hint", text: null };
}

/** Of the modules a change reaches into, the latest-filed one the typed time predates —
 *  `null` when there is none, or when the time is left for the server to set. */
export function filedAfter(
  occurredAt: string | null,
  modules: Iterable<Pick<ModuleRef, "archive_no" | "created_at">>,
): Pick<ModuleRef, "archive_no" | "created_at"> | null {
  let latest: Pick<ModuleRef, "archive_no" | "created_at"> | null = null;
  for (const module of modules) {
    if (!isEarlierThan(occurredAt, module.created_at)) continue;
    if (!latest || Date.parse(module.created_at ?? "") > Date.parse(latest.created_at ?? "")) {
      latest = module;
    }
  }
  return latest;
}
